import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { NavLink, Link } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import Collapse from "react-bootstrap/Collapse";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";
import config from "../../Config";
import StudentPanelMenu from "./StudentPanelMenu";
import StudentRightsCheck from "./StudentRightsCheck";
import { scrollToTop } from "../Shared/ScrollOnTop";
import imgAlerts from "../../assets/images/dashboard_icon/status.svg";

function StudentPanelAlerts() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const pageSize = 15;
  const [openId, setOpenId] = useState(null);

  const studentId = parseInt(UserID, 10) || 0;

  const loadList = useCallback(() => {
    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "StudentNotification/GetMyStudentNotifications",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID: studentId,
        MemberType: MemberType || "Student",
        Student_ID: studentId,
        pageNo,
        pageSize,
      },
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          setList(res.data.ListofRecords || []);
          setTotalRecords(res.data.totalRecords || 0);
          setUnreadCount(res.data.unread_count ?? 0);
        } else {
          setList([]);
          setTotalRecords(0);
          setUnreadCount(0);
          if (res.data.status_message) {
            toast.error(res.data.status_message, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        }
      })
      .catch(() => {
        setList([]);
        toast.error("Could not load notifications.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setLoading(false));
  }, [AccessKey, UserID, MemberType, studentId, pageNo]);

  useEffect(() => {
    document.title = "Alerts and Updates - Student Panel";
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    setOpenId(null);
  }, [pageNo]);

  const markSeen = (id) => {
    if (!id) return;
    axios({
      method: "post",
      url: config.base_url + "StudentNotification/MarkStudentNotificationSeen",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID: studentId,
        MemberType: MemberType || "Student",
        Student_ID: studentId,
        ID: id,
      },
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          setList((prev) =>
            prev.map((row) =>
              row.ID === id
                ? { ...row, Is_Read: true, Seen_At: moment().format() }
                : row
            )
          );
          setUnreadCount((c) => Math.max(0, c - 1));
        }
      })
      .catch(() => {});
  };

  const toggleOpen = (row) => {
    const id = row.ID;
    const next = openId === id ? null : id;
    setOpenId(next);
    if (next === id && !row.Is_Read) {
      markSeen(id);
    }
  };

  return (
    <>
      <StudentPanelMenu />
      <ToastContainer />
      <StudentRightsCheck />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/student-panel/dashboard" onClick={scrollToTop}>
                        Dashboard
                      </Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Alerts and updates
                    </li>
                  </ol>
                </nav>
                <div className="page-title d-flex align-items-center gap-3 flex-wrap">
                  <img src={imgAlerts} alt="" style={{ width: 40, height: 40 }} />
                  <span>Alerts and updates</span>
                  {unreadCount > 0 && (
                    <span className="badge bg-danger rounded-pill">{unreadCount} new</span>
                  )}
                </div>
                <p className="text-muted small mb-0">
                  Messages from school appear here after they are approved. Tap a
                  notification to read the full text.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content mt-3 pb-5">
          <div className="container">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status" />
              </div>
            ) : list.length === 0 ? (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <img
                    src={imgAlerts}
                    alt=""
                    className="mb-3 opacity-50"
                    style={{ maxWidth: "72px" }}
                  />
                  <h5 className="text-muted">No notifications yet</h5>
                  <p className="text-muted small mb-0">
                    When your school publishes an update for you, it will show up
                    here.
                  </p>
                  <NavLink
                    to="/student-panel/dashboard"
                    className="btn btn-primary mt-3"
                    onClick={scrollToTop}
                  >
                    Back to dashboard
                  </NavLink>
                </div>
              </div>
            ) : (
              <>
                <div className="d-flex flex-column gap-2">
                  {list.map((row) => {
                    const expanded = openId === row.ID;
                    const isNew = !row.Is_Read;
                    return (
                      <div
                        key={row.ID}
                        className={`card border-0 shadow-sm ${isNew ? "border-start border-4 border-primary" : ""}`}
                      >
                        <button
                          type="button"
                          className="card-body text-start bg-transparent border-0 w-100 p-3"
                          onClick={() => toggleOpen(row)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <div>
                              <div className="fw-semibold mb-1">
                                {row.Title}
                                {isNew && (
                                  <span className="badge bg-primary ms-2">New</span>
                                )}
                              </div>
                              <div className="small text-muted">
                                {row.Created_Date
                                  ? moment(row.Created_Date).format(
                                      "DD MMM YYYY, hh:mm A"
                                    )
                                  : ""}
                                {row.Sent_By && (
                                  <span>
                                    {" "}
                                    · From {row.Sent_By}
                                    {row.Sent_By_Type
                                      ? ` (${row.Sent_By_Type})`
                                      : ""}
                                  </span>
                                )}
                              </div>
                            </div>
                            <i
                              className={`ri-arrow-${expanded ? "up" : "down"}-s-line fs-4 text-muted`}
                            />
                          </div>
                        </button>
                        <Collapse in={expanded}>
                          <div className="px-3 pb-3 pt-0 border-top border-light">
                            <p className="mb-0 mt-2" style={{ whiteSpace: "pre-wrap" }}>
                              {row.Details}
                            </p>
                            {(row.Wing_Name || row.Class_Name || row.Section_Name) && (
                              <p className="small text-muted mt-2 mb-0">
                                {row.Wing_Name}
                                {row.Class_Name
                                  ? ` · ${row.Class_Name}`
                                  : ""}
                                {row.Section_Name
                                  ? ` · ${row.Section_Name}`
                                  : ""}
                              </p>
                            )}
                          </div>
                        </Collapse>
                      </div>
                    );
                  })}
                </div>

                {totalRecords > pageSize && (
                  <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={pageNo <= 1}
                      onClick={() => setPageNo((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                    <span className="text-muted small">
                      Page {pageNo} · {totalRecords} total
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={pageNo * pageSize >= totalRecords}
                      onClick={() => setPageNo((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default StudentPanelAlerts;
